"use client";

import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/app/components/ui/card";
import { Button } from "@/app/components/ui/button";
import { Textarea } from "@/app/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/app/components/ui/tabs";
import { Copy, RotateCcw, FileText, GitCompare } from "lucide-react";
import { toast } from "react-hot-toast";

export default function DiffCheckerTool() {
  const [text1, setText1] = useState("");
  const [text2, setText2] = useState("");
  const [diffResult, setDiffResult] = useState("");

  const calculateDiff = () => {
    if (!text1.trim() || !text2.trim()) {
      toast.error("Please enter both texts to compare");
      return;
    }

    const lines1 = text1.split('\n');
    const lines2 = text2.split('\n');
    
    let result = "";
    let i = 0, j = 0;
    
    while (i < lines1.length || j < lines2.length) {
      if (i >= lines1.length) {
        result += `+ ${lines2[j]}\n`;
        j++;
      } else if (j >= lines2.length) {
        result += `- ${lines1[i]}\n`;
        i++;
      } else if (lines1[i] === lines2[j]) {
        result += `  ${lines1[i]}\n`;
        i++;
        j++;
      } else {
        // Check if line was moved
        let found = false;
        for (let k = j + 1; k < lines2.length; k++) {
          if (lines1[i] === lines2[k]) {
            result += `+ ${lines2[j]}\n`;
            j++;
            found = true;
            break;
          }
        }
        if (!found) {
          result += `- ${lines1[i]}\n`;
          i++;
        }
      }
    }
    
    setDiffResult(result);
  };

  const copyDiff = () => {
    if (!diffResult) return;
    navigator.clipboard.writeText(diffResult)
      .then(() => toast.success("Diff copied to clipboard!"))
      .catch(() => toast.error("Failed to copy diff."));
  };

  const clearAll = () => {
    setText1("");
    setText2("");
    setDiffResult("");
  };

  const loadSample = () => {
    setText1(`Hello World!
This is the first version.
It has some content.
And more lines here.`);

    setText2(`Hello World!
This is the second version.
It has different content.
And some new lines here.
Plus an additional line.`);
  };

  const getHighlightedDiff = () => {
    if (!diffResult) return "";

    return diffResult.split('\n').map((line, index) => {
      if (line.startsWith('+')) {
        return `<div class="bg-green-100 dark:bg-green-900 p-1 rounded text-green-800 dark:text-green-200">${line}</div>`;
      } else if (line.startsWith('-')) {
        return `<div class="bg-red-100 dark:bg-red-900 p-1 rounded text-red-800 dark:text-red-200">${line}</div>`;
      } else {
        return `<div class="p-1">${line}</div>`;
      }
    }).join('');
  };

  return (
    <div className="space-y-6">
      <Tabs defaultValue="input" className="space-y-6">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="input">Input</TabsTrigger>
          <TabsTrigger value="diff">Diff Result</TabsTrigger>
          <TabsTrigger value="side-by-side">Side by Side</TabsTrigger>
        </TabsList>

        <TabsContent value="input" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="card-enhanced">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  Text 1 (Original)
                </CardTitle>
                <CardDescription>
                  Enter the first text to compare
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Textarea
                  placeholder="Enter the first text here..."
                  value={text1}
                  onChange={(e) => setText1(e.target.value)}
                  className="min-h-[300px] font-mono text-sm"
                />
              </CardContent>
            </Card>

            <Card className="card-enhanced">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  Text 2 (Modified)
                </CardTitle>
                <CardDescription>
                  Enter the second text to compare
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Textarea
                  placeholder="Enter the second text here..."
                  value={text2}
                  onChange={(e) => setText2(e.target.value)}
                  className="min-h-[300px] font-mono text-sm"
                />
              </CardContent>
            </Card>
          </div>

          <div className="flex justify-center gap-4">
            <Button onClick={calculateDiff} className="btn-enhanced hover-lift">
              <GitCompare className="h-4 w-4 mr-2" />
              Compare Texts
            </Button>
            <Button onClick={loadSample} variant="outline" className="hover-lift">
              Load Sample
            </Button>
            <Button onClick={clearAll} variant="outline" className="hover-lift">
              <RotateCcw className="h-4 w-4 mr-2" />
              Clear All
            </Button>
          </div>
        </TabsContent>

        <TabsContent value="diff" className="space-y-6">
          <Card className="card-enhanced">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <GitCompare className="h-5 w-5" />
                Diff Result
              </CardTitle>
              <CardDescription>
                Unified diff format showing changes between the texts
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="min-h-[400px] p-4 border rounded-lg bg-muted/20 font-mono text-sm whitespace-pre-wrap">
                {diffResult || "Click 'Compare Texts' to see the diff result"}
              </div>
              
              {diffResult && (
                <Button onClick={copyDiff} variant="outline" className="hover-lift">
                  <Copy className="h-4 w-4 mr-2" />
                  Copy Diff
                </Button>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="side-by-side" className="space-y-6">
          <Card className="card-enhanced">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <GitCompare className="h-5 w-5" />
                Side by Side Comparison
              </CardTitle>
              <CardDescription>
                Visual comparison with highlighted differences
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div 
                className="min-h-[400px] p-4 border rounded-lg bg-muted/20 font-mono text-sm"
                dangerouslySetInnerHTML={{ 
                  __html: getHighlightedDiff() || "<p class='text-muted-foreground'>Click 'Compare Texts' to see the comparison</p>" 
                }}
              />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <Card className="card-enhanced">
        <CardHeader>
          <CardTitle>Diff Legend</CardTitle>
          <CardDescription>
            Understanding the diff output format
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="flex items-center gap-3 p-3 bg-green-100 dark:bg-green-900 rounded-lg">
              <div className="text-green-800 dark:text-green-200 font-mono">+</div>
              <div>
                <div className="font-semibold text-green-800 dark:text-green-200">Added</div>
                <div className="text-sm text-green-600 dark:text-green-300">Lines present in text 2 but not in text 1</div>
              </div>
            </div>
            <div className="flex items-center gap-3 p-3 bg-red-100 dark:bg-red-900 rounded-lg">
              <div className="text-red-800 dark:text-red-200 font-mono">-</div>
              <div>
                <div className="font-semibold text-red-800 dark:text-red-200">Removed</div>
                <div className="text-sm text-red-600 dark:text-red-300">Lines present in text 1 but not in text 2</div>
              </div>
            </div>
            <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
              <div className="font-mono"> </div>
              <div>
                <div className="font-semibold">Unchanged</div>
                <div className="text-sm text-muted-foreground">Lines that are identical in both texts</div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
